// Spanish configs for the Rockwall County SEO cluster.
// Mirrors rockwallSeoConfigs.js with translated copy. Each export pairs with
// an English counterpart via `langAlt` for hreflang.
import {
  CITIES, ROCKWALL_HUB_PATH,
  TIRZEPATIDE_HUB_PATH, SEMAGLUTIDE_HUB_PATH,
} from "./rockwallSeo";

// ────────── Spanish equivalents of shared FAQs ──────────
const SHARED_FAQS_ES = (cityName) => [
  {
    q: `¿Está BestRx disponible para los residentes de ${cityName}, TX?`,
    a: `Sí. BestRx es una plataforma de telemedicina con licencia en Texas que atiende todos los códigos postales del condado de Rockwall, incluido ${cityName}. La revisión de su proveedor se realiza en línea por un clínico con licencia en Texas, y su medicamento se envía desde una farmacia magistral con licencia directamente a su dirección en ${cityName}.`,
  },
  {
    q: `¿Qué medicamentos para perder peso pueden obtener los residentes de ${cityName}?`,
    a: `Los proveedores de BestRx pueden recetar Tirzepatida compuesta, Semaglutida compuesta, formulaciones orales de GLP-1, e inyectables de apoyo como MIC+B12 y L-Carnitina — cuando sea clínicamente apropiado según su evaluación.`,
  },
  {
    q: `¿Necesito seguro para comenzar un programa de pérdida de peso de BestRx en ${cityName}?`,
    a: `No. BestRx es un programa de pago directo. No hay copagos, preautorizaciones ni complicaciones con seguros. Nuevos pacientes pueden comenzar desde $39 la primera semana*.`,
  },
  {
    q: `¿Cuánto tarda en llegar mi medicamento en ${cityName}?`,
    a: `La mayoría de los pacientes de ${cityName} completan la evaluación, reciben la revisión del proveedor, y reciben el medicamento dentro de 3 a 5 días hábiles. Envío expedito disponible.`,
  },
  {
    q: `¿Es confidencial la consulta?`,
    a: `Sí. Todas las comunicaciones de BestRx siguen estándares HIPAA. El medicamento se envía en empaque sin marcas, sin indicación exterior de su contenido.`,
  },
  {
    q: `¿Qué pasa si tengo efectos secundarios?`,
    a: `Su proveedor asignado de BestRx está disponible durante todo su programa. Ajustes de dosis, manejo de efectos secundarios y cambios de protocolo se hacen directamente por el portal del paciente — sin visita adicional.`,
  },
];

// Spanish city name mapping (only those differing)
const CITY_NAMES_ES = {
  "rockwall": "Rockwall",
  "royse-city": "Royse City",
  "fate": "Fate",
  "heath": "Heath",
  "mclendon-chisholm": "McLendon-Chisholm",
  "mobile-city": "Mobile City",
};

/* ════════════════════════  ROCKWALL COUNTY HUB (ES)  ════════════════════════ */
export const rockwallCountyHubConfigEs = {
  path: "/es/rockwall-county-weight-loss",
  lang: "es",
  langAlt: { en: "/rockwall-county-weight-loss", es: "/es/rockwall-county-weight-loss" },
  title: "Pérdida de Peso en el Condado de Rockwall — Tirzepatida, Semaglutida y GLP-1",
  metaDescription:
    "Pérdida de peso médica que atiende a todas las ciudades del condado de Rockwall — Rockwall, Royse City, Fate, Heath, McLendon-Chisholm, Mobile City. Tirzepatida y Semaglutida revisadas por un proveedor, enviadas a su puerta. Desde $39 la semana 1*.",
  eyebrow: "Atendiendo a todas las ciudades del condado de Rockwall, Texas",
  h1: "Pérdida de Peso del Condado de Rockwall — Tirzepatida, Semaglutida y GLP-1",
  heroLead:
    "BestRx es la autoridad en pérdida de peso médica en línea del condado de Rockwall. Proveedores con licencia en Texas, medicamentos GLP-1 compuestos y entrega discreta de farmacia — atendiendo a Rockwall, Royse City, Fate, Heath, McLendon-Chisholm y Mobile City. Comience su evaluación en 7 minutos. Nuevos pacientes comienzan desde $39 la primera semana*.",
  breadcrumbs: [
    { label: "Inicio", to: "/" },
    { label: "Pérdida de Peso del Condado de Rockwall" },
  ],
  areaServed: ["Rockwall", "Royse City", "Fate", "Heath", "McLendon-Chisholm", "Mobile City"],
  showTirzVsSema: true,
  sections: [
    {
      h2: "Por qué los residentes del condado de Rockwall eligen BestRx",
      body: [
        "El condado de Rockwall ha crecido un 70% en la última década. La infraestructura de salud no ha mantenido el ritmo. La mayoría de las clínicas locales tienen esperas de semanas para nuevos pacientes, y las clínicas de pérdida de peso existentes generalmente operan como spas médicos — sin revisión de proveedor con licencia, sin supervisión de farmacia magistral, y sin seguimiento.",
        "BestRx está construido de manera diferente. Cada paciente es revisado por un médico o enfermero practicante con licencia en Texas antes de cualquier receta. Cada receta es preparada por una farmacia magistral con licencia 503A. Y cada paciente tiene acceso directo y continuo a su proveedor asignado — sin agendar ni una sola cita en persona.",
        "Hemos visto a residentes manejar 45 minutos a Dallas, esperar en estacionamientos para telemedicina, y pagar más de $400 al mes por medicamentos GLP-1 que podrían obtener en casa por una fracción del costo. BestRx resuelve los tres problemas: evaluación más rápida, precio inicial más bajo, y cero traslado.",
      ],
      bullets: [
        "Revisión por proveedor con licencia en Texas — cada paciente, cada receta",
        "Cumplimiento por farmacia magistral con licencia 503A",
        "$39 la primera semana — transparencia total en precios continuos",
        "Mensajería directa al proveedor a través del portal del paciente",
        "Envío discreto en empaque sin marcas a su dirección en el condado de Rockwall",
        "Sin preautorizaciones de seguro, sin copagos, sin sorpresas",
      ],
    },
    {
      h2: "Medicamentos para pérdida de peso disponibles para pacientes del condado de Rockwall",
      body: [
        "Su proveedor de BestRx puede recetar la gama completa de medicamentos médicos para pérdida de peso cuando sea clínicamente apropiado. Los dos más solicitados son Tirzepatida compuesta y Semaglutida compuesta — los mismos ingredientes activos que se encuentran en Mounjaro/Zepbound y Ozempic/Wegovy, preparados por una farmacia magistral con licencia.",
      ],
      bullets: [
        "Tirzepatida compuesta (5 mg, 7.5 mg, 10 mg, 12.5 mg por 2 mL)",
        "Semaglutida compuesta (titulación de 0.25 mg a 2.4 mg semanal)",
        "GLP-1 oral (alternativas compuestas de Semaglutida oral)",
        "Inyecciones lipotrópicas MIC+B12",
        "Inyecciones de L-Carnitina (apoyo metabólico)",
        "Suplementos de apoyo cuando sean apropiados",
      ],
    },
    {
      h2: "Nuestro proceso de telemedicina en 4 pasos",
      body: [
        "Desde su primer clic hasta su primera inyección, BestRx está diseñado para velocidad sin atajos. La mayoría de los pacientes del condado de Rockwall reciben su medicamento dentro de 3 a 5 días hábiles después de completar la evaluación.",
      ],
      bullets: [
        "Paso 1 — Complete la evaluación (≈ 7 minutos)",
        "Paso 2 — Revisión del proveedor (dentro de 24 horas)",
        "Paso 3 — Preparación en farmacia magistral",
        "Paso 4 — Entrega + atención continua a través del portal",
      ],
    },
    {
      h2: "Atendiendo a cada ciudad del condado de Rockwall",
      body: [
        "BestRx es el único proveedor de pérdida de peso médica construido específicamente para la población distribuida del condado de Rockwall. Atendemos cada código postal — desde el centro histórico de Rockwall hasta las nuevas construcciones de Fate, las propiedades junto al lago en Heath, el corredor de crecimiento de Royse City y el lujo rural de McLendon-Chisholm.",
      ],
      bullets: CITIES.map((c) => `${c.name}, TX (${c.population})`),
    },
    {
      h2: "Por qué BestRx supera a clínicas locales y telemedicina nacional",
      body: [
        "Los spas médicos y clínicas locales de pérdida de peso en el condado de Rockwall típicamente cuestan $400-$700 al mes, requieren visitas en persona, y no incluyen supervisión continua del proveedor. Marcas nacionales de telemedicina como Ro, Hims, y WeightWatchers Clinic ofrecen conveniencia pero operan a escala — sin relación específica con proveedor de Texas, protocolos genéricos de dosis, y flexibilidad limitada en preparación.",
        "BestRx ofrece lo mejor de ambos: proveedores con licencia en Texas que conocen a los pacientes texanos, precios transparentes de pago directo que superan a cada competidor nacional que hemos comparado, y los mismos medicamentos GLP-1 compuestos respaldados por una instalación 503A.",
      ],
    },
  ],
  faqs: [
    {
      q: "¿A cuáles ciudades del condado de Rockwall atiende BestRx?",
      a: "A todas — Rockwall, Royse City, Fate, Heath, McLendon-Chisholm, y Mobile City. Como BestRx es 100% telemedicina, el código postal solo importa para el envío, no para el acceso al proveedor.",
    },
    {
      q: "¿Cuánto cuesta la pérdida de peso médica en el condado de Rockwall a través de BestRx?",
      a: "Nuevos pacientes comienzan desde $39 la primera semana*. Los precios mensuales continuos dependen del medicamento y la dosis. Publicamos precios transparentes durante la evaluación — sin sorpresas, sin contratos.",
    },
    {
      q: "¿Aceptan seguro para pérdida de peso en el condado de Rockwall?",
      a: "BestRx es un programa de pago directo. Los medicamentos GLP-1 compuestos generalmente no están cubiertos por seguros, sin importar el proveedor. Nuestros precios en efectivo típicamente superan el costo total de copago + visita clínica.",
    },
    {
      q: "¿Tirzepatida o Semaglutida es mejor para los pacientes del condado de Rockwall?",
      a: "Depende del IMC inicial, experiencia previa con GLP-1, y presupuesto. Tirzepatida (agonista dual GIP+GLP-1) produce una reducción de peso corporal del 15-22% en promedio en 12 meses. Semaglutida produce 10-15%. Su proveedor de BestRx le recomendará el medicamento correcto después de revisar su evaluación.",
    },
    {
      q: "¿Qué tan rápido puedo comenzar si vivo en Rockwall, TX?",
      a: "La mayoría de los pacientes de Rockwall, Royse City, Fate, Heath, y McLendon-Chisholm son revisados dentro de 24 horas y reciben su medicamento en 3 a 5 días hábiles.",
    },
    ...SHARED_FAQS_ES("Condado de Rockwall"),
  ],
};

/* ════════════════════════  CITY PAGE FACTORY (ES)  ════════════════════════ */
export function buildCityConfigEs(citySlug) {
  const c = CITIES.find((x) => x.slug === citySlug);
  if (!c) throw new Error("Unknown city: " + citySlug);
  const nameEs = CITY_NAMES_ES[citySlug] || c.name;
  return {
    path: `/es${c.path}`,
    lang: "es",
    langAlt: { en: c.path, es: `/es${c.path}` },
    title: `Pérdida de Peso en ${nameEs} — Tirzepatida y Semaglutida`,
    metaDescription:
      `Pérdida de peso médica para residentes de ${nameEs}, TX. Proveedores con licencia en Texas, Tirzepatida y Semaglutida compuestas, enviadas a su dirección en ${nameEs}. Desde $39 la semana 1*.`,
    eyebrow: `${nameEs}, Texas · Condado de Rockwall`,
    h1: `Pérdida de Peso en ${nameEs} — Tirzepatida y Semaglutida con Revisión Médica`,
    heroLead:
      `BestRx es la autoridad en pérdida de peso médica para ${nameEs}, TX (${c.population}). Revisión por proveedor con licencia en Texas, medicamentos GLP-1 compuestos, y entrega discreta de farmacia — todo en línea. Proceso 100% telemedicina diseñado para residentes de ${nameEs}.`,
    breadcrumbs: [
      { label: "Inicio", to: "/" },
      { label: "Condado de Rockwall", to: "/es/rockwall-county-weight-loss" },
      { label: `Pérdida de Peso en ${nameEs}` },
    ],
    areaServed: [c.name],
    showTirzVsSema: true,
    sections: [
      {
        h2: `Por qué ${nameEs} elige BestRx`,
        body: [
          `${nameEs} es una comunidad activa donde la conveniencia importa. La mayoría de los residentes que buscan "pérdida de peso cerca de mí" encuentran una de tres opciones: un spa médico en Rockwall, una clínica en Dallas, o una marca nacional de telemedicina. Ninguna está construida para el estilo de vida específico de ${nameEs}.`,
          `BestRx es la alternativa — pérdida de peso médica supervisada por un proveedor con la conveniencia de telemedicina completa, diseñada y con precios para residentes de Texas.`,
          `Cada receta viene de un clínico con licencia en Texas que revisa su evaluación completa. Cada medicamento se envía desde una farmacia magistral 503A. Y cada paciente mantiene contacto con su proveedor a través de nuestro portal de mensajería HIPAA — sin tarifas extras, sin salas de espera.`,
        ],
        bullets: [
          `Atiende todos los códigos postales de ${nameEs}`,
          `Revisión por proveedor la misma semana para nuevos pacientes`,
          `Tirzepatida y Semaglutida compuestas`,
          `Envío directo a su dirección en ${nameEs} — empaque sin marcas`,
          `Proveedores con licencia en Texas, no telemedicina genérica nacional`,
        ],
      },
      {
        h2: `Vecindarios de ${nameEs} que atendemos`,
        body: [`Desde calles históricas hasta nuevas construcciones, BestRx envía a cada vecindario de ${nameEs} — incluyendo:`],
        bullets: c.neighborhoods,
      },
      {
        h2: `Puntos de referencia locales que nuestros pacientes de ${nameEs} conocen`,
        body: [`Si es residente de mucho tiempo o nuevo en el área, reconocerá estos puntos de referencia cerca de nuestras direcciones de entrega más comunes en ${nameEs}:`],
        bullets: c.landmarks,
      },
      {
        h2: `Medicamentos disponibles para pacientes de ${nameEs}`,
        body: [
          `Su proveedor de BestRx puede recetar el formulario completo de pérdida de peso médica cuando sea clínicamente apropiado — Tirzepatida compuesta, Semaglutida compuesta, alternativas orales de GLP-1, e inyectables de apoyo como MIC+B12 y L-Carnitina.`,
        ],
        bullets: [
          "Tirzepatida compuesta (5 / 7.5 / 10 / 12.5 mg por 2 mL)",
          "Semaglutida compuesta (titulación de 0.25 a 2.4 mg semanal)",
          "GLP-1 oral (formulaciones orales compuestas de semaglutida)",
          "Inyecciones lipotrópicas MIC+B12",
          "Inyecciones de apoyo de L-Carnitina",
        ],
      },
      {
        h2: `Qué esperar — proceso de telemedicina para ${nameEs}`,
        body: [
          `Los pacientes de ${nameEs} completan el proceso completo en línea. No hay visita en persona, ni viaje a Dallas, ni estacionamiento. Su evaluación toma 7 minutos, su revisión de proveedor sucede dentro de 24 horas, y su medicamento llega en 3 a 5 días hábiles.`,
        ],
        bullets: [
          "Paso 1 — Evaluación en línea de 7 minutos",
          "Paso 2 — Revisión por proveedor con licencia en Texas dentro de 24 horas",
          "Paso 3 — Farmacia magistral prepara su receta",
          `Paso 4 — Envío discreto a su dirección en ${nameEs}`,
          "Continuo — mensajee a su proveedor directamente para ajustes",
        ],
      },
    ],
    faqs: [
      {
        q: `¿Dónde está la clínica BestRx más cercana a ${nameEs}, TX?`,
        a: `BestRx no opera una clínica física — y ese es el punto. Somos 100% en línea para residentes de ${nameEs}. La revisión de su proveedor con licencia en Texas y la entrega de medicamentos vienen a usted. Sin viaje, sin sala de espera.`,
      },
      {
        q: `¿Puede mi proveedor en ${nameEs} acceder a mis registros de BestRx?`,
        a: `Sí. A solicitud compartimos resúmenes de visita, historial de recetas, y resultados de laboratorio con cualquier proveedor de atención primaria existente en ${nameEs} o en otro lugar — sin costo adicional.`,
      },
      ...SHARED_FAQS_ES(nameEs),
    ],
  };
}
